/**
 * Menu Toggle Module
 * Provides scoped toggle functionality for toggle groups using data-toggle-group attribute
 */
class MenuToggleManager {
  constructor() {
    this.toggleGroups = new Map();
    this.brandsMenuHandlers = new Map();
    this.init();
  }

  /**
   * Initialize the menu toggle system
   */
  init() {
    this.initializeToggleGroups();
    this.bindEventListeners();
  }

  /**
   * Find and initialize all toggle groups on the page
   */
  initializeToggleGroups() {
    const toggleGroups = document.querySelectorAll('[data-toggle-group]');

    toggleGroups.forEach((group, index) => {
      const groupId = group.id || `toggle-group-${index}`;
      group.id = groupId;

      this.toggleGroups.set(groupId, {
        element: group,
        toggles: group.querySelectorAll('[data-menu-toggle]'),
        targets: group.querySelectorAll('[data-menu-target], [data-collection-target]'),
      });
    });
  }

  /**
   * Bind event listeners to all toggle buttons
   */
  bindEventListeners() {
    document.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-menu-toggle]');
      if (!toggle) return;

      const menuId = toggle.getAttribute('data-menu-toggle');
      const toggleGroup = toggle.closest('[data-toggle-group]');

      if (toggleGroup) {
        this.handleToggleClick(menuId, toggleGroup);
      }
    });
  }

  /**
   * Handle toggle button click
   * @param {string} menuId - The ID of the menu to toggle
   * @param {HTMLElement} toggleGroup - The toggle group element
   */
  handleToggleClick(menuId, toggleGroup) {
    const groupId = toggleGroup.id;
    const groupData = this.toggleGroups.get(groupId);

    if (!groupData) return;

    // Special handling for brands menu
    if (menuId === 'brands-menu-desktop') {
      this.handleBrandsMenu(menuId, toggleGroup);
    } else {
      this.handleRegularMenu(menuId, toggleGroup);
    }
    document.body.classList.remove('header--hidden');
  }

  /**
   * Handle regular menu toggle
   * @param {string} menuId - The ID of the menu to toggle
   * @param {HTMLElement} toggleGroup - The toggle group element
   */
  handleRegularMenu(menuId, toggleGroup) {
    // Get toggles and targets within this specific group
    const groupToggles = toggleGroup.querySelectorAll('[data-menu-toggle]');
    const groupTargets = toggleGroup.querySelectorAll('[data-menu-target], [data-collection-target]');

    // Remove active state from all buttons in this group
    groupToggles.forEach((toggle) => {
      toggle.setAttribute('data-active', 'false');
    });

    // Hide all menu targets in this group
    groupTargets.forEach((target) => {
      target.setAttribute('data-active', 'false');
    });

    // Add active state to clicked button
    const activeToggles = toggleGroup.querySelectorAll(`[data-menu-toggle="${menuId}"]`);
    activeToggles.forEach((toggle) => {
      toggle.setAttribute('data-active', 'true');
    });

    // Show target menu - check both data-menu-target and data-collection-target
    const targetMenus = toggleGroup.querySelectorAll(
      `[data-menu-target="${menuId}"], [data-collection-target="${menuId}"]`
    );
    targetMenus.forEach((target) => {
      target.setAttribute('data-active', 'true');
    });
  }

  /**
   * Handle brands menu toggle with special behavior
   * @param {string} menuId - The ID of the menu to toggle
   * @param {HTMLElement} toggleGroup - The toggle group element
   */
  handleBrandsMenu(menuId, toggleGroup) {
    const groupId = toggleGroup.id;
    const groupData = this.toggleGroups.get(groupId);

    if (!groupData) return;

    // Toggle the brands menu state
    const isActive = (groupData.brandsMenuActive = !groupData.brandsMenuActive);

    // Update toggles and targets within this group
    const activeToggles = toggleGroup.querySelectorAll(`[data-menu-toggle="${menuId}"]`);
    activeToggles.forEach((toggle) => {
      toggle.setAttribute('data-active', isActive);
    });

    const targetMenus = toggleGroup.querySelectorAll(
      `[data-menu-target="${menuId}"], [data-collection-target="${menuId}"]`
    );
    targetMenus.forEach((target) => {
      target.setAttribute('data-active', isActive);
    });

    // Set up outside click and mouse leave handlers if not already done
    if (isActive && !groupData.brandsMenuHandler) {
      this.setupBrandsMenuHandlers(menuId, toggleGroup);
    }
  }

  /**
   * Set up special handlers for brands menu (outside click, mouse leave)
   * @param {string} menuId - The ID of the menu
   * @param {HTMLElement} toggleGroup - The toggle group element
   */
  setupBrandsMenuHandlers(menuId, toggleGroup) {
    const groupId = toggleGroup.id;
    const groupData = this.toggleGroups.get(groupId);

    if (!groupData) return;

    const brandsMenu = toggleGroup.querySelector(
      `[data-menu-target="${menuId}"], [data-collection-target="${menuId}"]`
    );
    const brandsToggle = toggleGroup.querySelector(`[data-menu-toggle="${menuId}"]`);

    if (!brandsMenu || !brandsToggle) return;

    const closeBrandsMenu = () => {
      // Hide brands menu
      brandsMenu.setAttribute('data-active', 'false');
      brandsToggle.setAttribute('data-active', 'false');

      // Remove event listeners
      brandsMenu.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('click', handleOutsideClick);

      // Reset state
      groupData.brandsMenuActive = false;
      groupData.brandsMenuHandler = null;
    };

    const handleMouseLeave = () => {
      closeBrandsMenu();
    };

    const handleOutsideClick = (event) => {
      const isClickInsideBrandsMenu = brandsMenu.contains(event.target);
      const isClickInsideBrandsToggle = brandsToggle.contains(event.target);

      if (!isClickInsideBrandsMenu && !isClickInsideBrandsToggle) {
        closeBrandsMenu();
      }
    };

    // Store the cleanup function
    groupData.brandsMenuHandler = closeBrandsMenu;

    // Add event listeners
    brandsMenu.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('click', handleOutsideClick);
  }

  /**
   * Add a new toggle group dynamically
   * @param {HTMLElement} toggleGroup - The toggle group element to add
   */
  addToggleGroup(toggleGroup) {
    const groupId = toggleGroup.id || `toggle-group-${Math.random().toString(36).substr(2, 9)}`;
    toggleGroup.id = groupId;

    this.toggleGroups.set(groupId, {
      element: toggleGroup,
      toggles: toggleGroup.querySelectorAll('[data-menu-toggle]'),
      targets: toggleGroup.querySelectorAll('[data-menu-target], [data-collection-target]'),
      brandsMenuActive: false,
      brandsMenuHandler: null,
    });
  }

  /**
   * Remove a toggle group
   * @param {string} groupId - The ID of the group to remove
   */
  removeToggleGroup(groupId) {
    const groupData = this.toggleGroups.get(groupId);
    if (groupData && groupData.brandsMenuHandler) {
      groupData.brandsMenuHandler();
    }
    this.toggleGroups.delete(groupId);
  }
}

/**
   * Discount Code Copy
   * Handles copying discount codes to clipboard with visual feedback
   */
class DiscountCodeCopy {
  constructor() {
    this.resetTimeout = null;
    this.init();
  }

  /**
   * Initialize the discount code copy system
   */
  init() {
    this.bindEventListeners();
  }

  /**
   * Bind click event listener for discount code buttons
   */
  bindEventListeners() {
    document.addEventListener('click', (event) => {
      const discountButton = event.target.closest('[data-code]');
      if (!discountButton) return;

      this.handleCopyClick(discountButton);
    });
  }

  /**
   * Handle click on discount code button
   * @param {HTMLElement} button - The discount button element
   */
  handleCopyClick(button) {
    const code = button.getAttribute('data-code');
    if (!code) return;

    this.copyToClipboard(code, button);
  }

  /**
   * Copy code to clipboard and show feedback
   * @param {string} code - The discount code to copy
   * @param {HTMLElement} button - The button element to update
   */
  copyToClipboard(code, button) {
    navigator.clipboard
      .writeText(code)
      .then(() => {
        this.showSuccessFeedback(button);
      })
      .catch((err) => {
        console.error('Failed to copy code:', err);
      });
  }

  /**
   * Show success feedback and restore original content
   * @param {HTMLElement} button - The button element to update
   */
  showSuccessFeedback(button) {
    // Store original content
    const originalContent = button.innerHTML;

    // Clear any existing timeout
    if (this.resetTimeout) {
      clearTimeout(this.resetTimeout);
    }

    // Show success message
    button.innerHTML = '<span class="flex items-center justify-center"><span>Copied discount code to Clipboard!</span></span>';

    // Reset after 2 seconds
    this.resetTimeout = setTimeout(() => {
      button.innerHTML = originalContent;
      this.resetTimeout = null;
    }, 2000);
  }
}

// ReelUp section magnify effect
document.addEventListener('DOMContentLoaded', function () {
  // Initialize menu toggle and discount code functionality
  window.menuToggleManager = new MenuToggleManager();
  window.discountCodeCopy = new DiscountCodeCopy();

  const initializeReelUpMagnification = () => {
    const reelUpCarouselSections = document.querySelectorAll('[id*="__reelup_shoppable_videos_reels_"]');

    if (reelUpCarouselSections.length === 0) return false;

    const carousel = reelUpCarouselSections[0];
    // const slides = carousel.querySelectorAll('.reelUp_slide.video_initialized');
    const slides = carousel.querySelectorAll('.reelUp_slide[data-product-id]');

    // Check if slides are initialized
    if (slides.length === 0) return false;

    // Find the carousel wrapper
    // const carouselWrapper = carousel.querySelector('.reelUp_carousel_wrapper');
    const carouselWrapper = carousel.querySelector('.reelUp_carousel_list');
    if (!carouselWrapper) return false;

    // Custom scrollbar indicator support for all browsers
    const updateScrollIndicator = () => {
      // console.log('rca debug: updateScrollIndicator ');
      const scrollLeft = carouselWrapper.scrollLeft;
      const scrollWidth = carouselWrapper.scrollWidth;
      const clientWidth = carouselWrapper.clientWidth;
      const maxScroll = scrollWidth - clientWidth;
      const scrollPercentage = maxScroll > 0 ? (scrollLeft / maxScroll) * 80 : 0; // 80% because thumb is 20% width
      document.body.style.setProperty('--scroll-percentage', scrollPercentage);
    };

    // console.log('rca debug: carouselWrapper: ', carouselWrapper);

    carouselWrapper.addEventListener('scroll', updateScrollIndicator);
    updateScrollIndicator(); // Initial call

    let currentMagnifiedSlide = null;
    let heightLocked = false;

    const updateMagnification = () => {
      // Re-query slides in case they've changed
      // const currentSlides = carousel.querySelectorAll('.reelUp_slide.video_initialized');
      const currentSlides = carousel.querySelectorAll('.reelUp_slide[data-product-id]');

      const viewportCenter = window.innerWidth / 2;
      const isMobile = window.innerWidth < 768;
      const magnifiedWidth = isMobile ? '230px' : '300px';
      let closestSlide = null;
      let minDistance = Infinity;

      // Find slide closest to viewport center that's actually visible
      currentSlides.forEach((slide) => {
        const slideRect = slide.getBoundingClientRect();

        // Skip slides that are off-screen
        if (slideRect.right < 0 || slideRect.left > window.innerWidth) return;

        const slideCenter = slideRect.left + slideRect.width / 2;
        const distance = Math.abs(slideCenter - viewportCenter);

        if (distance < minDistance) {
          minDistance = distance;
          closestSlide = slide;
        }
      });

      // If the closest slide hasn't changed, do nothing
      if (closestSlide === currentMagnifiedSlide) return;

      // Remove magnification from all slides immediately
      currentSlides.forEach((slide) => {
        slide.style.removeProperty('width');
      });

      // Apply magnification to the closest slide
      if (closestSlide) {
        closestSlide.style.setProperty('width', magnifiedWidth, 'important');

        // Lock the height after first magnification to prevent jumps
        if (!heightLocked && carouselWrapper.offsetHeight > 100) {
          setTimeout(() => {
            const wrapperHeight = carouselWrapper.offsetHeight;
            carouselWrapper.style.setProperty('height', `${wrapperHeight}px`, 'important');
            heightLocked = true;
          }, 350); // Wait for transition to complete
        }
      }

      currentMagnifiedSlide = closestSlide;
    };

    // Initial magnification
    updateMagnification();

    // Find the actual scrollable container (might be carousel wrapper or a child)
    const scrollContainer = carouselWrapper.querySelector('.reelUp_cards_carousel') || carouselWrapper;

    // Update on scroll with throttling
    let scrollTimeout;
    scrollContainer.addEventListener('scroll', () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(updateMagnification, 10);
    });

    // Also update on window resize
    window.addEventListener('resize', updateMagnification);

    return true; // Successfully initialized
  };

  // Try to initialize immediately
  if (initializeReelUpMagnification()) {
    return; // Success, we're done
  }

  // Otherwise, set up MutationObserver to wait for video initialization
  const observer = new MutationObserver((_, obs) => {
    // Check if we now have initialized slides
    if (initializeReelUpMagnification()) {
      obs.disconnect(); // Stop observing once initialized
    }
  });

  // Start observing the entire document for changes
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class'],
  });

  // Also try again after a delay as a fallback
  setTimeout(() => {
    if (initializeReelUpMagnification()) {
      observer.disconnect();
    }
  }, 3000);
});
